// Server layer names the same keys (`src-server/abTesting/`) → else the test half-runs.
const splitUrlFeatureKey = "split-url";
const splitUrlMapFeatureKey = "split-url-map";

// `serverEffect`: what Nitro can do with it — 302 (`redirect`), cache-keyed SSR value
// (`render`), nothing (`none`).
// Targeting safety is separate — answered per payload by `isServerSafeFeature`.
/* eslint-disable @typescript-eslint/naming-convention -- GrowthBook feature keys are kebab-case */
const abTestFeatures = {
  [splitUrlFeatureKey]: { serverEffect: "redirect" },
  [splitUrlMapFeatureKey]: { serverEffect: "redirect" },
  "checkout-hide-distractions": { serverEffect: "none" },
  "form-group-variant-id": { serverEffect: "none" },
  "form-group-variant-name": { serverEffect: "none" },
  "form-header": { serverEffect: "none" },
  "form-jumplinks": { serverEffect: "none" },
  "form-translations": { serverEffect: "none" },
  "form-variant-id": { serverEffect: "none" },
  "form-variant-name": { serverEffect: "none" },
  "forms-auto-label-header": { serverEffect: "none" },
  "forms-centered-style": { serverEffect: "none" },
  "forms-compact-profile": { serverEffect: "none" },
  "forms-contact-info-icons": { serverEffect: "none" },
  "forms-cross-sell": { serverEffect: "none" },
  "forms-end-lead-animation": { serverEffect: "none" },
  "forms-end-stepper-at-end-lead": { serverEffect: "none" },
  "forms-phone-country-select": { serverEffect: "none" },
  "forms-radio-auto-step": { serverEffect: "none" },
  "forms-radio-button-style": { serverEffect: "none" },
  "forms-recognize-known-user": { serverEffect: "none" },
  "forms-review-stars": { serverEffect: "none" },
  "forms-segment-stepper": { serverEffect: "none" },
  "forms-split-profile": { serverEffect: "none" },
  "forms-substeps": { serverEffect: "none" },
  "jumplink-nav": { serverEffect: "none" },
  "link-map": { serverEffect: "none" },
  "main-site-homepage-location-search": { serverEffect: "render" },
  "main-site-products-navigation": { serverEffect: "render" },
  "main-site-products-navigation-config": { serverEffect: "render" },
  "satellites-form-popup": { serverEffect: "none" },
  "sticky-menu-desktop": { serverEffect: "none" },
  translations: { serverEffect: "render" },
  "whatsapp-opt-in": { serverEffect: "none" },
  "you-should-ask-chat": { serverEffect: "none" },
} as const;
/* eslint-enable @typescript-eslint/naming-convention */

type AbTestFeatureName = keyof typeof abTestFeatures;

type ServerRenderFeatureName = {
  [Key in AbTestFeatureName]: (typeof abTestFeatures)[Key]["serverEffect"] extends "render"
    ? Key
    : never;
}[AbTestFeatureName];

const serverRenderFeatureKeys: readonly ServerRenderFeatureName[] = (
  Object.keys(abTestFeatures) as AbTestFeatureName[]
).filter(
  (key): key is ServerRenderFeatureName =>
    abTestFeatures[key].serverEffect === "render",
);

type RenderFeatureValues = Partial<Record<ServerRenderFeatureName, unknown>>;

type AnyAbTestFeatures = Record<string, { serverEffect: string } | undefined>;

// Unregistered key → false: an unknown feature gets no server effect.
function hasServerEffect(key: string): boolean {
  const effect = (abTestFeatures as AnyAbTestFeatures)[key]?.serverEffect;
  return effect === "redirect" || effect === "render";
}

function isServerRenderFeature(key: string): boolean {
  return (abTestFeatures as AnyAbTestFeatures)[key]?.serverEffect === "render";
}

export type { AbTestFeatureName, RenderFeatureValues, ServerRenderFeatureName };
export {
  abTestFeatures,
  hasServerEffect,
  isServerRenderFeature,
  serverRenderFeatureKeys,
  splitUrlFeatureKey,
  splitUrlMapFeatureKey,
};
